import { useState, useEffect, useMemo, useRef } from 'react';
import Card from '../components/common/Card.jsx';
import Table, { UtilBar } from '../components/common/Table.jsx';
import MultiSelect from '../components/common/MultiSelect.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import LineChart from '../components/charts/LineChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import GroupedBarChart from '../components/charts/GroupedBarChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, RecommendedCostCell, SavingCell, RecCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, parseSaving, formatSavingStr,
  buildBaseCostKpis, exportCSV,
  getScopedItem, setScopedItem, STORAGE_KEYS,
} from '../components/common/Helper';

import RecommendationApiService from '../services/RecommendationApiService';
import InventoryApiService from '../services/InventoryApiService';
import APIConstants from '../services/APIConstants';
import { useAuth } from '../state/AuthContext';

// ── Tabs ─────────────────────────────────────────────────────

const subTabs = [
  { label: 'Combined Recommendations' },
  { label: 'CloudsAnalytics Recommendations' },
  { label: 'Azure Recommendations' },
];

const VIEW_TABS = [
  { key: 'all',       label: 'Servers' },
  { key: 'scheduled', label: 'Scheduled Stop/Start' },
  { key: 'idle',      label: 'Idle Servers' },
  { key: 'reserved',  label: 'Reserved Coverage' },
];

// ── Rec helpers ──────────────────────────────────────────────

const resolveDbRec = (recType) => {
  const t = (recType ?? '').toUpperCase().trim();
  let recPillClass = 'rec-ok';
  if (['STOP', 'DELETE', 'REMOVE', 'REMOVEUNUSEDRESOURCE', 'TERMINATE', 'DEALLOCATE'].includes(t))     recPillClass = 'rec-id';
  else if (['DOWNSIZE', 'RIGHTSIZE', 'DOWNGRADE'].includes(t))                                        recPillClass = 'rec-rs';
  else if (['SCHEDULE', 'SCHEDULESTART', 'SCHEDULESTOP', 'SCHEDULESCALEIN'].includes(t))              recPillClass = 'rec-warn';
  else if (['BUY', 'BUY_RI', 'BUY_RESERVED', 'PURCHASE_RI', 'RESERVED', 'RI', 'BUY 1-YR RI'].includes(t)) recPillClass = 'rec-buy';
  return { recType: t || 'OPTIMAL', recPillClass };
};

// ── Row Mappers ──────────────────────────────────────────────

const toMySQLRow = (item, source) => {
  const current     = parseCost(item.currentMonthlyPriceValue ?? item.currentDiskMonthlyPriceValue);
  const recommended = parseCost(item.recommendedMonthlyPriceValue ?? 0);
  const saving      = parseCost(item.savingCost ?? item.savingsAmount ?? (current - recommended));
  const rawRec      = item.recommendationType ?? item.remark ?? (saving > 0 ? 'DOWNSIZE' : 'OPTIMAL');
  const { recType, recPillClass } = resolveDbRec(rawRec);

  // Prefer the backend's human-readable detail, then fall back through the other
  // remark-style keys, and finally to the resolved rec type. `source` is kept for
  // any per-source tweaks but all APIs share the same `recommendationDetail` field.
  const notNA = (v) => (v && String(v).trim().toUpperCase() !== 'NA') ? v : null;
  const remark =
    notNA(item.recommendationDetail) ??
    notNA(item.recommendationSubCategory) ??
    notNA(item.customRecomendationRemark) ??
    notNA(item.remark) ??
    (recType === 'OPTIMAL' ? 'Optimal' : recType);

  // CPU & Connections
  const cpu = item.avgCpuValue ?? item.cpuUtilization ?? item.avgCpuPercentage ?? item.averageCpuUsage ?? null;
  const peakCpu = item.peakCpuValue ?? null;
  const p95Cpu = item.p95CpuValue ?? null;
  const p99Cpu = item.p99CpuValue ?? null;
  const connections = item.avgConnections ?? item.avgConnectionsValue ?? null;
  const status = item.state ?? item.status ?? '—';

  return {
    id: item.id,
    dbName: item.serverName ?? item.dbName ?? item.name ?? '—',
    location: item.location ?? '—',
    resourceGroup: item.resourceGroup ?? '—',
    version: item.version ?? item.engineVersion ?? item.mysqlVersion ?? '—',
    sku: item.skuName ?? item.sku ?? item.currentSkuName ?? '—',
    skuTier: item.skuTier ?? item.tierCode ?? '—',
    tier: detectTier(item.skuTier ?? item.tierCode ?? item.skuName ?? item.sku ?? item.currentSkuName),
    vCores: item.vCores ?? item.vcores ?? item.currentVcores ?? '—',
    cpu,
    peakCpu,
    p95Cpu,
    p99Cpu,
    connections,
    status,
    cost: `$${current.toFixed(2)}`,
    costValue: current,
    recommendedCost: recommended > 0 ? `$${recommended.toFixed(2)}` : '—',
    recommendedCostValue: recommended,
    recommendationRemark: remark,
    recType,
    recPillClass,
    saving: formatSavingStr(saving),
    actionLabel: 'Assist with AI',
    actionClass: 'mini-btn ai-assist',
  };
};

const mapCustomRows   = (data) => data.map((item) => toMySQLRow(item, 'custom'));
const mapAzureRows    = (data) => data.map((item) => toMySQLRow(item, 'azure'));
const mapCombinedRows = (data) => data.map((item) => toMySQLRow(item, 'combined'));

// ── Tier Detection ────────────────────────────────────────────

// Accepts either the backend's explicit skuTier/tierCode (e.g. "Burstable") or a
// raw SKU string (e.g. "Standard_B1ms") and normalizes to one of three buckets.
const detectTier = (skuOrTier) => {
  const s = (skuOrTier ?? '').toLowerCase();
  if (s.includes('business') || s.includes('(bc)') || s.includes('business_critical') || s.includes('businesscritical') || s.includes('bc_')) return 'Business Critical';
  if (s.includes('burstable') || s.includes('(burstable)') || /_b\d/i.test(s) || s.startsWith('b')) return 'Burstable';
  return 'General Purpose';
};

// ── KPI ──────────────────────────────────────────────────────

const buildKpiData = (totalMonthlyCost, totalCount, rows) => {
  const rowCurrent  = rows.reduce((s, r) => s + r.costValue, 0);
  const rowRec      = rows.reduce((s, r) => s + r.recommendedCostValue, 0);
  const current     = totalMonthlyCost != null ? parseCost(totalMonthlyCost.currentCost)     : rowCurrent;
  const recommended = totalMonthlyCost != null ? parseCost(totalMonthlyCost.recommendedCost) : rowRec;
  const saving      = totalMonthlyCost != null ? parseCost(totalMonthlyCost.savingCost)      : Math.max(0, current - recommended);
  const gpCount     = rows.filter((r) => r.tier === 'General Purpose').length;
  const bCount      = rows.filter((r) => r.tier === 'Burstable').length;

  return buildBaseCostKpis({
    countTitle: 'MySQL Servers',
    countValue: totalCount,
    countSubtitle: `${gpCount} GP · ${bCount} Burstable`,
    current, saving, recommended,
  });
};

// ── Charts ────────────────────────────────────────────────────

const buildTrendChartData = (rows) => {
  const active = rows
    .filter((r) => (r.costValue ?? 0) > 0 || (r.cpu != null && r.cpu > 0))
    .map((r) => ({ name: r.dbName, cpu: r.cpu ?? 0, cost: (r.costValue ?? 0) / 30 }))
    .sort((a, b) => b.cost - a.cost || b.cpu - a.cpu)
    .slice(0, 10);
  if (!active.length) return null;
  return {
    labels: active.map((r) => r.name),
    datasets: [
      { label: 'Avg CPU %', data: active.map((r) => r.cpu), borderColor: '#0078d4', backgroundColor: 'rgba(0,120,212,0.15)', fill: true, tension: 0.3, yAxisID: 'y1', borderWidth: 2, pointRadius: 3 },
      { label: 'Daily Cost ($)', data: active.map((r) => parseFloat(r.cost.toFixed(2))), borderColor: '#f57c00', fill: false, tension: 0.3, yAxisID: 'y2', borderWidth: 2, pointRadius: 3, borderDash: [4, 3] },
    ],
  };
};

const buildCpuProfileChartData = (rows) => {
  const active = rows
    .filter((r) => r.cpu != null || r.peakCpu != null)
    .sort((a, b) => (b.peakCpu ?? b.cpu ?? 0) - (a.peakCpu ?? a.cpu ?? 0))
    .slice(0, 8);
  if (!active.length) return null;
  return {
    labels: active.map((r) => r.dbName),
    datasets: [
      { label: 'Avg',  data: active.map((r) => r.cpu     ?? 0), backgroundColor: '#0078d4', borderRadius: 3, barPercentage: 0.75 },
      { label: 'P95',  data: active.map((r) => r.p95Cpu  ?? 0), backgroundColor: '#26c6da', borderRadius: 3, barPercentage: 0.75 },
      { label: 'P99',  data: active.map((r) => r.p99Cpu  ?? 0), backgroundColor: '#7e57c2', borderRadius: 3, barPercentage: 0.75 },
      { label: 'Peak', data: active.map((r) => r.peakCpu ?? 0), backgroundColor: '#f57c00', borderRadius: 3, barPercentage: 0.75 },
    ],
  };
};

const buildSkuChartData = (rows) => {
  if (!rows.length) return null;
  const map = {};
  const hasCost = rows.some((r) => r.costValue > 0);
  rows.forEach((r) => { if (r.sku && r.sku !== '—') map[r.sku] = (map[r.sku] || 0) + (hasCost ? r.costValue : 1); });
  const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  if (!sorted.length) return null;
  return { labels: sorted.map(([c]) => c), datasets: [{ label: hasCost ? 'Cost ($)' : 'Count', data: sorted.map(([, v]) => parseFloat(v.toFixed(2))), backgroundColor: '#0078d4', borderRadius: 4, barPercentage: 0.6 }] };
};

const buildTierDonut = (rows) => {
  if (!rows.length) return null;
  const hasCost = rows.some((r) => r.costValue > 0);
  const buckets = { 'General Purpose': 0, 'Burstable': 0, 'Business Critical': 0 };
  rows.forEach((r) => { const t = r.tier; if (t in buckets) buckets[t] = (buckets[t] || 0) + (hasCost ? r.costValue : 1); });
  const entries = Object.entries(buckets).filter(([, v]) => v > 0);
  if (!entries.length) return null;
  return {
    labels: entries.map(([m]) => m),
    datasets: [{ data: entries.map(([, v]) => parseFloat(v.toFixed(2))), backgroundColor: ['#0078d4', '#2ea66f', '#8e24aa'], borderWidth: 2, borderColor: '#fff' }],
  };
};

const buildInsightActions = (rows) => {
  const RI_RECS = ['BUY', 'BUY_RI', 'BUY_RESERVED', 'PURCHASE_RI', 'RESERVED', 'RI', 'BUY 1-YR RI'];
  const withSaving = rows
    .filter((r) => parseSaving(r.saving) > 0)
    .map((r) => ({ ...r, savingNum: parseSaving(r.saving) }))
    .sort((a, b) => b.savingNum - a.savingNum);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingNum, 0);
  const actions = withSaving.map((r, i) => {
    let actionLabel = 'Review →', actionClass = 'mini-btn';
    if (['DOWNSIZE', 'RIGHTSIZE', 'DOWNGRADE'].includes(r.recType))              { actionLabel = 'Rightsize →'; }
    else if (['STOP', 'SCHEDULE', 'SCHEDULESTART', 'SCHEDULESTOP'].includes(r.recType)) { actionLabel = 'Schedule →'; actionClass = 'mini-btn ghost'; }
    else if (RI_RECS.includes(r.recType))                                        { actionLabel = 'Buy RI →';    actionClass = 'mini-btn ghost'; }
    return { id: i + 1, title: r.dbName || r.id, amount: r.saving, desc: `${r.sku} · ${r.location}`, actionLabel, actionClass, row: r };
  });
  return { actions, totalSaving };
};

// ── View-tab filter ───────────────────────────────────────────

const RI_RECS = ['BUY', 'BUY_RI', 'BUY_RESERVED', 'PURCHASE_RI', 'RESERVED', 'RI', 'BUY 1-YR RI'];

const applyViewFilter = (rows, viewKey) => {
  if (viewKey === 'scheduled') return rows.filter((r) => ['SCHEDULE', 'SCHEDULESTART', 'SCHEDULESTOP', 'SCHEDULESCALEIN'].includes(r.recType));
  if (viewKey === 'idle')      return rows.filter((r) => ['STOP', 'DELETE', 'REMOVE', 'REMOVEUNUSEDRESOURCE'].includes(r.recType));
  if (viewKey === 'reserved')  return rows.filter((r) => RI_RECS.includes(r.recType));
  return rows;
};

// ── Table Columns ─────────────────────────────────────────────

const dbColumns = [
  { key: 'dbName',         label: 'Server Name' },
  { key: 'version',        label: 'Version' },
  { key: 'sku',            label: 'SKU (Tier)', render: (row) => (row.skuTier && row.skuTier !== '—') ? `${row.sku} (${row.skuTier})` : row.sku },
  {
    key: 'status',         label: 'Status',
    render: (row) => {
      const isAvail = ['available', 'running', 'ready', 'active', 'enabled'].includes((row.status ?? '').toLowerCase().trim());
      return <span className={`status-pill ${isAvail ? 'st-avail' : 'st-stopped'}`}>{row.status}</span>;
    }
  },
  { key: 'cpu',            label: 'CPU Avg',          render: (row) => <UtilBar avgValue={row.cpu} peakValue={row.peakCpu} /> },
  { key: 'p99Cpu',         label: 'P99 CPU',          className: 'num', render: (row) => row.p99Cpu != null ? <span>{row.p99Cpu}%</span> : <span style={{ color: 'var(--muted)' }}>—</span> },
  { key: 'p95Cpu',         label: 'P95 CPU',          className: 'num', render: (row) => row.p95Cpu != null ? <span>{row.p95Cpu}%</span> : <span style={{ color: 'var(--muted)' }}>—</span> },
  { key: 'cost',           label: 'Cost / Mo',        className: 'num', render: (row) => <CostCell row={row} /> },
  { key: 'recommendationRemark', label: 'Recommendation', render: (row) => <RecCell row={row} /> },
  { key: 'saving',         label: 'Saving / Mo',      className: 'num', render: (row) => <SavingCell row={row} /> },
  { key: 'recommendedCost',label: 'Payable Cost / Mo',className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
  {
    key: 'action',         label: 'Action',
    render: (row) => (
      <button className={row.actionClass} onClick={row.onAction ? () => row.onAction(row) : undefined}>
        {row.actionLabel}
      </button>
    ),
  },
];

// ── Main Page ─────────────────────────────────────────────────

const AzureDbMySQLOptimization = () => {
  const { selectedAccount } = useAuth();

  const [activeTab, setActiveTab] = useState(0);
  const [viewTab,   setViewTab]   = useState('all');

  const [isLoadingCombined, setIsLoadingCombined] = useState(false);
  const [isLoadingCustom, setIsLoadingCustom] = useState(false);
  const [isLoadingAzure,  setIsLoadingAzure]  = useState(false);

  const [combinedRows, setCombinedRows] = useState([]);
  const [customRows, setCustomRows] = useState([]);
  const [azureRows,  setAzureRows]  = useState([]);

  const [combinedKpi, setCombinedKpi] = useState([]);
  const [customKpi, setCustomKpi] = useState([]);
  const [azureKpi,  setAzureKpi]  = useState([]);


  const [trendData,      setTrendData]      = useState(null);
  const [tierData,       setTierData]       = useState(null);
  const [cpuProfileData, setCpuProfileData] = useState(null);
  const [skuData,        setSkuData]        = useState(null);

  const [insightActions, setInsightActions] = useState([]);
  const [insightTotal,   setInsightTotal]   = useState(0);

  const [tagList,        setTagList]        = useState([]);
  const [filterList,     setFilterList]     = useState({ tag: '', values: [] });
  const [versionFilter,  setVersionFilter]  = useState('');
  const [tierFilter,     setTierFilter]     = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  const [aiOpen,   setAiOpen]   = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData,   setAiData]   = useState(null);

  const getUserId = () => {
    const raw = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    if (!raw) return null;
    try { return JSON.parse(raw)?.userId ?? null; } catch { return null; }
  };

  const handleDetailsClick = (row) => {
    setAiData({
      dbId: row.id, dbName: row.dbName, location: row.location, resourceGroup: row.resourceGroup,
      version: row.version, sku: row.sku, vCores: row.vCores, avgCpu: row.cpuUtil,
      currentMonthlyCost: row.cost, recommendation: row.recType, estimatedMonthlySaving: row.saving,
    });
    setAiPrompt(
      'You are a cloud cost advisor. Based on the MySQL server data provided, give a concise, natural insight ' +
      'directly to the user — not a formal report. Mention the cost situation, savings within reach, ' +
      'and what they should consider doing. Keep it conversational, friendly, and to the point.'
    );
    setAiOpen(true);
  };

  const fetchTagList = () => {
    if (!selectedAccount) return;
    setTagList([]);
    if (filterList.tag !== '' || filterList.values.length > 0) setFilterList({ tag: '', values: [] });
    InventoryApiService.getAzureFilterTagList(
      APIConstants.ENDPOINT_AZURE_FILTER_TAG,
      { type: 'MYSQL_DATABASE', userAccountId: selectedAccount }
    )
      .then((res) => setTagList(res?.data ?? []))
      .catch(() => {});
  };

  const fetchedTabsRef = useRef(new Set());

  const fetchCombinedData = (userId) => {
    setIsLoadingCombined(true);
    setCombinedRows([]);
    RecommendationApiService.getAzureData({
      userId, type: 'COMBINED_MYSQL_FLEXIBLE_DATABASE', userAccountId: selectedAccount
    })
      .then((res) => {
        const rows = mapCombinedRows(res?.data ?? []);
        setCombinedRows(rows);
        setCombinedKpi(buildKpiData(res?.totalMonthlyCost, rows.length, rows));
      })
      .catch((err) => {
        console.error('Error fetching combined recommendations', err);
      })
      .finally(() => setIsLoadingCombined(false));
  };

  const fetchCustomData = (userId) => {
    setIsLoadingCustom(true);
    setCustomRows([]);
    RecommendationApiService.getAzureData({
      userId, type: 'CUSTOM_MYSQL_FLEXIBLE_DATABASE', userAccountId: selectedAccount,
      filterKey: filterList.tag || undefined,
      filterValues: filterList.values.length > 0 ? filterList.values : undefined,
    })
      .then((res) => {
        const rows = mapCustomRows(res?.data ?? []);
        setCustomRows(rows);
        setCustomKpi(buildKpiData(res?.totalMonthlyCost, rows.length, rows));
      })
      .catch((err) => {
        console.error('Error fetching custom recommendations', err);
      })
      .finally(() => setIsLoadingCustom(false));
  };

  const fetchAzureData = (userId) => {
    setIsLoadingAzure(true);
    setAzureRows([]);
    RecommendationApiService.getAzureData({ userId, type: 'MYSQL_FLEXIBLE_DATABASE', userAccountId: selectedAccount })
      .then((res) => {
        const rows = mapAzureRows(res?.data ?? []);
        setAzureRows(rows);
        setAzureKpi(buildKpiData(res?.totalMonthlyCost, rows.length, rows));
      })
      .catch((err) => {
        console.error('Error fetching azure recommendations', err);
      })
      .finally(() => setIsLoadingAzure(false));
  };

  const fetchTabData = (tabIndex, isForceRefresh = false) => {
    if (!selectedAccount) return;
    if (!isForceRefresh && fetchedTabsRef.current.has(tabIndex)) return;
    fetchedTabsRef.current.add(tabIndex);

    const userId = getUserId();
    if (!userId) return;

    if (tabIndex === 0) {
      fetchCombinedData(userId);
    } else if (tabIndex === 1) {
      fetchCustomData(userId);
    } else if (tabIndex === 2) {
      fetchAzureData(userId);
    }
  };

  const handleTabChange = (i) => {
    setActiveTab(i);
    fetchTabData(i);
  };

  const handleRefresh = () => {
    if (fetchedTabsRef.current) {
      fetchedTabsRef.current.delete(activeTab);
    }
    fetchTabData(activeTab, true);
    fetchTagList();
  };

  const handleFilterChange = (field, value) => {
    if (field === 'tag') { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: typeof value === 'string' ? value.split(',') : (value ?? []) }));
  };

  useEffect(() => {
    if (selectedAccount) {
      setFilterList({ tag: '', values: [] });
      fetchedTabsRef.current = new Set();
      setActiveTab(0);
      fetchTabData(0);
      fetchTagList();
    }
  }, [selectedAccount]);

  // Refetch CloudsAnalytics (custom) data only while its tab is active — the tag
  // filters are server-side. Guarding on activeTab keeps the custom API from
  // firing on mount alongside the default Combined fetch (only combined should call).
  useEffect(() => {
    if (activeTab !== 1 || !selectedAccount) return;
    if (filterList.tag === '' || filterList.values.length > 0) {
      const userId = getUserId();
      if (userId) {
        fetchedTabsRef.current.add(1);
        fetchCustomData(userId);
      }
    }
  }, [filterList]);

  const activeKpi       = activeTab === 0 ? combinedKpi       : activeTab === 1 ? customKpi       : azureKpi;
  const isLoadingActive = activeTab === 0 ? isLoadingCombined : activeTab === 1 ? isLoadingCustom : isLoadingAzure;
  const baseRows        = activeTab === 0 ? combinedRows      : activeTab === 1 ? customRows      : azureRows;

  const versions  = useMemo(() => [...new Set(baseRows.map((r) => r.version).filter((v) => v && v !== '—'))].sort(), [baseRows]);
  const tiers     = useMemo(() => [...new Set(baseRows.map((r) => r.tier))].sort(), [baseRows]);
  const locations = useMemo(() => [...new Set(baseRows.map((r) => r.location).filter(Boolean))].sort(), [baseRows]);

  const filteredRows = useMemo(() => {
    let rows = applyViewFilter(baseRows, viewTab);
    if (versionFilter)  rows = rows.filter((r) => r.version === versionFilter);
    if (tierFilter)     rows = rows.filter((r) => r.tier === tierFilter);
    if (locationFilter) rows = rows.filter((r) => r.location === locationFilter);
    return rows;
  }, [baseRows, viewTab, versionFilter, tierFilter, locationFilter]);

  const activeRows = filteredRows.map((row) => ({ ...row, onAction: handleDetailsClick }));

  useEffect(() => {
    if (filteredRows.length > 0) {
      setTrendData(buildTrendChartData(filteredRows));
      setTierData(buildTierDonut(filteredRows));
      setCpuProfileData(buildCpuProfileChartData(filteredRows));
      setSkuData(buildSkuChartData(filteredRows));
      const { actions, totalSaving } = buildInsightActions(filteredRows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setTrendData(null);
      setTierData(null);
      setCpuProfileData(null);
      setSkuData(null);
      setInsightActions([]);
      setInsightTotal(0);
    }
  }, [filteredRows]);

  // Keep AI chatbot context in sync with the data shown on this page
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'Azure MySQL Optimization',
      kpi: activeKpi,
      mysqlRecommendations: filteredRows,
    });
  }, [filteredRows, activeKpi]);

  const handleExport = () =>
    exportCSV(activeRows, dbColumns.filter((c) => c.key !== 'action'), `azure-db-mysql-tab${activeTab}.csv`);

  const clearLocalFilters = () => {
    setVersionFilter(''); setTierFilter(''); setLocationFilter('');
    setFilterList({ tag: '', values: [] });
  };



  const donutOptions = useMemo(() => ({
    cutout: '60%', maintainAspectRatio: false,
    plugins: { legend: { position: 'right', labels: { font: { size: 11 }, boxWidth: 10, padding: 8 } }, tooltip: { callbacks: { label: (c) => `${c.label}: $${c.parsed.toLocaleString()}` } } },
  }), []);



  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> Azure DB{' '}
        <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>MySQL</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            MySQL Optimization{' '}
            {isLoadingActive ? <BadgeSkeleton /> : <span className="badge">{baseRows.length} servers</span>}
          </h1>
          <div className="meta">Azure Database for MySQL — Flexible Server · downsize, stop dev servers, reserve steady prod</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport}>⬇ Export</button>
          <button className="btn ghost" onClick={handleRefresh}>↻ Refresh</button>
        </div>
      </div>

      <KpiRow kpiData={activeKpi} loading={isLoadingActive} />

      {/* <div className="tab-row">
        {VIEW_TABS.map((t) => (
          <div key={t.key} className={`tab${viewTab === t.key ? ' active' : ''}`} onClick={() => setViewTab(t.key)}>
            {t.label}
          </div>
        ))}
      </div> */}

      <div className="grid-3">
        <Card className="first" title="MySQL utilization & cost" subtitle="CPU utilization vs daily cost (Top 10 servers)">
          <LineChart data={trendData} loading={isLoadingActive} chartType="line" y2BeginAtZero={false} xMaxRotation={0} y1Label="" y2Label="Daily Cost ($)" />
        </Card>
        <Card className="first" title="Cost by compute tier" subtitle="Burstable / General Purpose / Business Critical">
          <DonutChart data={tierData} loading={isLoadingActive} />
        </Card>
        <TopActionsCard title="Top MySQL actions" actions={insightActions} total={insightTotal} loading={isLoadingActive} entityName="servers" onAction={(item) => item.row && handleDetailsClick(item.row)} />
      </div>

      <div className="sub-tabs">
        {subTabs.map((t, i) => (
          <div key={i} className={`sub-tab${activeTab === i ? ' active' : ''}`} onClick={() => handleTabChange(i)}>
            {t.label}
          </div>
        ))}
      </div>

      <div className="filter-bar">
        <div className="field">
          <label>Filter Tag</label>
          <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
            <option value="">None</option>
            {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Filter Tag Values</label>
          <MultiSelect
            options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
            value={filterList.values}
            onChange={(vals) => handleFilterChange('values', vals)}
            disabled={!filterList.tag}
          />
        </div>
        <div className="field">
          <label>Version</label>
          <select value={versionFilter} onChange={(e) => setVersionFilter(e.target.value)}>
            <option value="">All</option>
            {versions.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Tier</label>
          <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
            <option value="">All</option>
            {tiers.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Location</label>
          <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="spacer" />
        {(filterList.tag || versionFilter || tierFilter || locationFilter) && (
          <button className="btn ghost" onClick={clearLocalFilters}>✕ Clear</button>
        )}
        <button className="btn ghost" onClick={handleRefresh}>↻ Refresh</button>
      </div>

      <Table
        rows={activeRows}
        columns={dbColumns}
        loading={isLoadingActive}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="saving"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText="No MySQL server recommendations"
      />

      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginTop: '20px', marginBottom: '4px' }}>
        Utilization
      </div>
      <div className="grid-2 grid-gap-top">
        <Card title="CPU profile — Avg / P95 / P99 / Peak" subtitle="Headroom analysis per server">
          <GroupedBarChart data={cpuProfileData} loading={isLoadingActive} />
        </Card>
        <Card title="Cost by SKU Tiers" subtitle="Top SKU families this month">
          <HorizontalBarChart data={skuData} loading={isLoadingActive} />
        </Card>
      </div>

      <AIModal open={aiOpen} onClose={() => setAiOpen(false)} prompt={aiPrompt} data={aiData} />
    </>
  );
};

export default AzureDbMySQLOptimization;
